/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Storefront, ArrowRight, ShieldCheck, EnvelopeSimple, Lock, ArrowLeft,
} from "@phosphor-icons/react";
import {
  partnerApi, setPartnerToken, fmtPartnerErr, isPartnerAuthed,
} from "../../lib/partnerApi";

const BG_GRADIENT = "linear-gradient(135deg, #0B2B66 0%, #1A4080 45%, #2D5A27 100%)";

export default function PartnerLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [ttl, setTtl] = useState(0);
  const otpRef = useRef(null);

  useEffect(() => {
    if (isPartnerAuthed()) nav("/service-partner/dashboard", { replace: true });
  }, []);
  useEffect(() => {
    if (!challenge) return;
    setTtl(challenge.ttl_seconds || 600);
    const t = setInterval(() => setTtl((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [challenge]);

  const submitPwd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await partnerApi.post("/service-partners/login",
                                       { email, password });
      setChallenge(r.data);
      setStep("otp");
      setTimeout(() => otpRef.current?.focus(), 100);
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };
  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await partnerApi.post("/service-partners/login/verify-otp",
                                       { challenge_id: challenge.challenge_id,
                                         otp: otp.trim() });
      setPartnerToken(r.data.access_token);
      toast.success(`Welcome, ${r.data.partner?.name || r.data.user?.name}`);
      nav(r.data.must_change_password
            ? "/service-partner/change-password"
            : "/service-partner/dashboard",
          { replace: true });
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  const mm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ss = String(ttl % 60).padStart(2, "0");

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="partner-login">
      {/* Left: brand storytelling */}
      <div className="relative hidden lg:flex flex-col p-12 text-white overflow-hidden"
           style={{ background: BG_GRADIENT }}>
        <div className="absolute inset-0 opacity-10"
              style={{ background:
                "radial-gradient(circle at 30% 20%, #C9A227 0%, transparent 40%), radial-gradient(circle at 80% 80%, #2D5A27 0%, transparent 50%)" }}/>
        <Link to="/" className="relative inline-flex items-center gap-2 text-xs text-white/70 hover:text-white">
          <ArrowLeft size={11}/> Back to website
        </Link>
        <div className="relative mt-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-[0.22em] font-semibold mb-6">
            <Storefront size={11} weight="fill"/> Service Partner Portal
          </div>
          <h1 className="font-heading text-5xl leading-tight">
            Power your <span className="italic text-[#C9A227]">retail · health · delivery</span> business with Rescigo.
          </h1>
          <p className="text-white/80 text-lg mt-6 max-w-md leading-relaxed">
            Manage your listings, fulfilment, appointments and payouts from a single
            partner dashboard. Backed by the same audited infrastructure trusted by
            government and healthcare clients across India.
          </p>
          <div className="mt-10 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em] text-white/60">
            <span className="px-3 py-1 rounded-full border border-white/20">GST · Compliant</span>
            <span className="px-3 py-1 rounded-full border border-white/20">PCI-aware</span>
            <span className="px-3 py-1 rounded-full border border-white/20">2-step verified</span>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 bg-[#F7F6F2]">
        <div className="max-w-md w-full mx-auto">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55]">Partner sign-in</div>
          <h2 className="font-heading text-3xl mt-1 text-[#1A1A1A]">
            {step === "password" ? "Welcome back." : "Verify it's you."}
          </h2>
          <p className="text-sm text-[#5C5A55] mt-2">
            {step === "password"
              ? "Sign in with your registered email and password."
              : `An 8-digit code was emailed to ${challenge?.email}. Enter it within 10 minutes.`}
          </p>

          {step === "password" ? (
            <form onSubmit={submitPwd} className="mt-7 space-y-4">
              <Field label="Email">
                <div className="relative">
                  <EnvelopeSimple size={13} className="absolute left-3 top-3.5 text-[#8A8780]"/>
                  <input type="email" required value={email}
                          autoFocus data-testid="login-email"
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="partner@yourbusiness.com"
                          className="w-full h-11 pl-9 pr-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]"/>
                </div>
              </Field>
              <Field label="Password">
                <div className="relative">
                  <Lock size={13} className="absolute left-3 top-3.5 text-[#8A8780]"/>
                  <input type="password" required value={password}
                          data-testid="login-password"
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Your temporary or chosen password"
                          className="w-full h-11 pl-9 pr-3 border border-[#E5E2D9] rounded-md bg-white text-sm outline-none focus:border-[#2D5A27]"/>
                </div>
              </Field>
              <button type="submit" disabled={busy} data-testid="login-submit"
                      className="w-full h-11 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? "Sending code…" : <>Continue <ArrowRight size={12} weight="bold"/></>}
              </button>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="mt-7 space-y-4">
              <Field label={`8-digit code · expires in ${mm}:${ss}`}>
                <input ref={otpRef} required value={otp} inputMode="numeric"
                        data-testid="login-otp" maxLength={8}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        placeholder="••••••••"
                        className="w-full h-12 px-3 border border-[#E5E2D9] rounded-md bg-white text-2xl font-mono tracking-[0.4em] text-center outline-none focus:border-[#2D5A27]"/>
              </Field>
              <button type="submit" disabled={busy || otp.length !== 8} data-testid="otp-submit"
                      className="w-full h-11 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? "Verifying…" : <>Verify & sign in <ShieldCheck size={13} weight="fill"/></>}
              </button>
              <button type="button" onClick={() => { setStep("password"); setOtp(""); }}
                      className="w-full text-xs text-[#5C5A55] hover:text-[#2D5A27] underline">
                Use a different email
              </button>
            </form>
          )}

          <div className="mt-12 pt-6 border-t border-[#E5E2D9] text-[11px] text-[#8A8780] leading-relaxed">
            Need help getting started? Contact your Rescigo account manager
            or write to <a href="mailto:partners@rescigo.health" className="underline text-[#2D5A27]">partners@rescigo.health</a>.
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-1.5">{label}</div>
    {children}
  </label>
);
